import React, { useImperativeHandle, useRef } from "react";
import CardTemplate from "components/card/card-template/CardTemplate";
import Tab from "components/tab/Tab";
import { DateOfBirthField, GenderField, StateField, NicotineUsageField, CoverageAmountSlider } from "components/form-fields";
import type { QuoteRef, QuoteValues, DTOApplicationFormReturn } from "types/Card";
import { toUsStates } from "constants/validations";

interface QuoteCardProps {
    className?: string;
    form: DTOApplicationFormReturn;
    // Quote form state
    activeTab: "myself" | "spouse";
    coverageOptions: Array<{ coverageAmount: number; monthlyPremium: number }>;
    selectedCoverage: number;
    isQuoteFormValid: boolean;
    isLoadingQuotes: boolean;
    // Computed values
    offerCoverageAmounts: number[];
    memberOfferAvailable: boolean;
    spouseOfferAvailable: boolean;
    currentPremium: number;
    displayName: string;
    isNewYorkSelected: boolean;
    // Form field values
    dobMonth?: string;
    dobDay?: string;
    dobYear?: string;
    gender?: string;
    stateValue?: string;
    nicotineUsage?: string;
    // Event handlers
    onTabChange: (tabId: string) => void;
    onCoverageChange: (value: number) => void;
    onApplyNow: () => void;
    onDateOfBirthBlur: () => void;
}

type QuoteCardWithRef = QuoteCardProps & {
    ref?: React.Ref<QuoteRef>;
};

const Quote = ({ ref, ...props }: QuoteCardWithRef) => {
    const {
        className = "",
        form,
        activeTab,
        coverageOptions,
        selectedCoverage,
        isQuoteFormValid,
        isLoadingQuotes,
        offerCoverageAmounts,
        memberOfferAvailable,
        spouseOfferAvailable,
        currentPremium,
        displayName,
        isNewYorkSelected,
        dobMonth,
        dobDay,
        dobYear,
        gender,
        stateValue,
        nicotineUsage,
        onTabChange,
        onCoverageChange,
        onApplyNow,
        onDateOfBirthBlur,
    } = props;
    // ========== Hooks ==========
    const {
        register,
        formState: { errors },
        trigger,
    } = form;
    const containerRef = useRef<HTMLFormElement>(null);

    // ========== JSX Constants ==========
    const tabs = [
        {
            id: "myself",
            label: "Myself",
            content: null,
            disabled: !memberOfferAvailable,
        },
        {
            id: "spouse",
            label: "My Spouse¹",
            content: null,
            disabled: !spouseOfferAvailable,
        },
    ];

    // ========== Imperative Handle ==========
    useImperativeHandle(
        ref,
        () => ({
            validate: () => trigger("quote"),
            getValues: (): QuoteValues => {
                const formData = form.getValues("quote");
                return {
                    applicantType: activeTab === "myself" ? "member" : "spouse",
                    dateOfBirth: formData.dateOfBirth,
                    gender: formData.gender as "male" | "female",
                    state: toUsStates(formData.state)!,
                    nicotineUsage: formData.nicotineUsage,
                    coverageAmount: selectedCoverage,
                    coveragePremium: currentPremium,
                };
            },
        }),
        [trigger, form, activeTab, selectedCoverage, currentPremium]
    );

    // ========== Render ==========
    return (
        <CardTemplate title={`${displayName}'s Life Insurance`} className={className}>
            <form
                ref={containerRef}
                className="flex flex-col gap-3"
                data-testid="quote-form"
                onSubmit={(e) => e.preventDefault()}
            >
                <p className="text-center text-sm font-semibold leading-5">Customize this quote for</p>

                <div className="flex justify-center">
                    <Tab tabs={tabs} defaultActiveTab={activeTab} onTabChange={onTabChange} showContent={false} />
                </div>

                <DateOfBirthField
                    monthRegister={register("quote.dateOfBirth.month")}
                    dayRegister={register("quote.dateOfBirth.day")}
                    yearRegister={register("quote.dateOfBirth.year")}
                    monthValue={dobMonth}
                    dayValue={dobDay}
                    yearValue={dobYear}
                    monthError={errors.quote?.dateOfBirth?.month?.message}
                    dayError={errors.quote?.dateOfBirth?.day?.message}
                    yearError={errors.quote?.dateOfBirth?.year?.message}
                    onBlur={onDateOfBirthBlur}
                    disabled={isLoadingQuotes}
                    id="quote-date-of-birth"
                />

                <GenderField
                    register={register("quote.gender")}
                    value={gender || undefined}
                    error={errors.quote?.gender?.message}
                    fullWidth={true}
                    disabled={isLoadingQuotes}
                    id="quote-gender"
                />

                <StateField
                    register={register("quote.state")}
                    value={stateValue || undefined}
                    error={isNewYorkSelected ? "Product not available" : errors.quote?.state?.message}
                    fullWidth={true}
                    disabled={isLoadingQuotes}
                    id="quote-state"
                />

                {!isNewYorkSelected && (
                    <NicotineUsageField
                        register={register("quote.nicotineUsage")}
                        value={nicotineUsage}
                        error={errors.quote?.nicotineUsage?.message}
                        fullWidth={true}
                        disabled={isLoadingQuotes}
                        id="quote-nicotine-usage"
                    />
                )}

                {isNewYorkSelected && (
                    <div className="bg-warning border border-warning rounded-lg p-4">
                        <p className="text-warning-content font-medium text-sm leading-5" style={{ fontFamily: "Open Sans" }}>
                            We're sorry, but the product you have selected is not available in your area at this time. Please
                            ensure that the state you entered is correct, or contact us at{" "}
                            <span className="font-sans font-bold text-sm leading-5 text-primary">(888) 422-7020</span> to learn
                            more about the life insurance options that may be available to you.
                        </p>
                    </div>
                )}

                {!isNewYorkSelected && (
                    <>
                        <CoverageAmountSlider
                            options={
                                coverageOptions.some((opt) => opt.monthlyPremium > 0)
                                    ? coverageOptions.map((opt) => ({
                                          value: opt.coverageAmount,
                                          label: `$${(opt.coverageAmount / 1000).toFixed(0)}k`,
                                          premium: opt.monthlyPremium,
                                      }))
                                    : [
                                          { value: 0, label: "-" },
                                          { value: 0, label: "-" },
                                          { value: 0, label: "-" },
                                          { value: 0, label: "-" },
                                      ]
                            }
                            value={selectedCoverage}
                            onChange={onCoverageChange}
                            disabled={offerCoverageAmounts.length === 0 || !isQuoteFormValid || isLoadingQuotes}
                        />

                        <div className="bg-base-200 rounded-2xl p-4 flex justify-between items-center">
                            <div className="flex flex-col">
                                <span className="font-sans font-semibold text-xl leading-7 text-base-content">
                                    {currentPremium === 0 ? "$-/month" : `$${currentPremium.toFixed(2)}/month`}
                                </span>
                                <span
                                    className="font-medium text-sm leading-5 text-base-content"
                                    style={{ fontFamily: "Open Sans" }}
                                >
                                    ${selectedCoverage.toLocaleString()} Life Insurance
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={onApplyNow}
                                disabled={
                                    isLoadingQuotes || coverageOptions.length === 0 || currentPremium === 0 || !isQuoteFormValid
                                }
                                className="btn btn-primary normal-case font-sans font-semibold text-sm leading-5 text-center align-middle text-secondary"
                            >
                                Apply Now
                                {isLoadingQuotes && (
                                    <span data-testid="loading-spinner" className="loading loading-spinner loading-sm" />
                                )}
                            </button>
                        </div>
                    </>
                )}
            </form>
        </CardTemplate>
    );
};

Quote.displayName = "Quote";

export default Quote;
